import React from "react";
import { LegalPageLayout, LegalSection } from "../../components/LegalPageLayout";

const sections = [
  { id: "acceptance", label: "Acceptance of terms" },
  { id: "service", label: "The service" },
  { id: "eligibility", label: "Eligibility & account" },
  { id: "subscription", label: "Subscription terms" },
  { id: "trials", label: "Free trials" },
  { id: "refunds", label: "Refund policy" },
  { id: "cancellation", label: "Cancellation policy" },
  { id: "sms-alerts", label: "SMS alerts" },
  { id: "user-responsibilities", label: "User responsibilities" },
  { id: "acceptable-use", label: "Acceptable use" },
  { id: "ip", label: "Intellectual property" },
  { id: "warranty-disclaimer", label: "Warranty disclaimer" },
  { id: "liability", label: "Limitation of liability" },
  { id: "indemnity", label: "Indemnification" },
  { id: "law", label: "Governing law & disputes" },
  { id: "changes", label: "Changes to these terms" },
  { id: "contact", label: "Contact" },
];

export default function TermsOfService() {
  return (
    <LegalPageLayout
      eyebrow="Legal · 02"
      title="Terms of Service"
      updated="February 20, 2026"
      intro="These Terms of Service govern your access to and use of EVIQ. By creating an account, subscribing, or connecting your Tesla, you agree to be bound by these terms. Please read them carefully."
      sections={sections}
    >
      <LegalSection id="acceptance" title="1. Acceptance of terms">
        <p>
          These Terms of Service (the &ldquo;<strong>Terms</strong>&rdquo;) form a legal
          agreement between you and EVIQ (&ldquo;<strong>EVIQ</strong>,&rdquo; &ldquo;
          <strong>we</strong>,&rdquo; &ldquo;<strong>us</strong>&rdquo;). By accessing
          <a href="https://eviq.pro"> eviq.pro</a>, creating an account, purchasing a
          subscription, or connecting your Tesla vehicle, you accept these Terms and our{" "}
          <a href="/privacy">Privacy Policy</a>. If you do not agree, do not use EVIQ.
        </p>
      </LegalSection>

      <LegalSection id="service" title="2. The service">
        <p>
          EVIQ is an independent battery-intelligence platform that ingests
          manufacturer-authorized vehicle telemetry (initially Tesla, via the official
          Tesla Fleet API) and produces predictive health scores, warranty-timeline
          projections, and optimization recommendations. EVIQ is <strong>not</strong>{" "}
          affiliated with, endorsed by, or sponsored by Tesla, Inc.
        </p>
      </LegalSection>

      <LegalSection id="eligibility" title="3. Eligibility & account">
        <p>
          You must be at least 16 years old and legally able to enter into contracts to
          use EVIQ. You are responsible for the accuracy of the information in your
          account and for keeping your login credentials secure. You must promptly notify
          us at <a href="mailto:support@eviq.pro">support@eviq.pro</a> if you suspect
          unauthorized access to your account.
        </p>
      </LegalSection>

      <LegalSection id="subscription" title="4. Subscription terms">
        <p>EVIQ is offered on two subscription plans:</p>
        <ul>
          <li>
            <strong>EVIQ Basic — Monthly:</strong> $9.99 per month, billed monthly, auto-renewing
            each month until you cancel.
          </li>
          <li>
            <strong>EVIQ Yearly:</strong> $99.99 per year, billed annually and auto-renewing
            until you cancel.
          </li>
          <li>
            <strong>EVIQ Lifetime:</strong> $199.99 one-time payment for lifetime access —
            no renewals, transferable to your next EV.
          </li>
        </ul>
        <p>
          All prices are in U.S. Dollars and exclude any applicable taxes. Your subscription
          is charged to the payment method on file via our PCI-DSS-compliant payment
          processor. If a charge fails, we will retry per the payment processor&rsquo;s
          standard dunning schedule, and access may be suspended if payment is not received.
        </p>
      </LegalSection>

      <LegalSection id="trials" title="5. Free trials">
        <p>
          EVIQ may offer a 7-day free trial. A valid payment method is required at
          sign-up. You will not be charged during the trial. If you do not cancel before
          the trial ends, your paid subscription automatically begins and your payment
          method will be charged the plan amount. You can cancel at any time during the
          trial by emailing <a href="mailto:support@eviq.pro">support@eviq.pro</a>{" "}
          — you will not be charged.
        </p>
      </LegalSection>

      <LegalSection id="refunds" title="6. Refund policy">
        <p>
          Because EVIQ is a digital subscription service:
        </p>
        <ul>
          <li>Payments are non-refundable except where required by applicable law.</li>
          <li>
            If you cancel during a paid billing period, you retain access through the end of
            that billing period. We do not issue pro-rated refunds for the unused portion of a
            monthly or annual term.
          </li>
          <li>
            We may, at our sole discretion, offer refunds for exceptional circumstances such
            as duplicate charges, extended service outages that prevented use, or clearly
            demonstrated billing errors. Email us at{" "}
            <a href="mailto:support@eviq.pro">support@eviq.pro</a> to request review.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="cancellation" title="7. Cancellation policy">
        <p>
          You may cancel your subscription at any time by sending an email from the address
          associated with your account to{" "}
          <a href="mailto:support@eviq.pro">support@eviq.pro</a> with the subject
          &ldquo;Cancel my subscription.&rdquo; We will process your cancellation within two
          (2) business days and confirm by reply email. Your subscription will not renew at
          the end of the current billing period; you retain access until then.
        </p>
      </LegalSection>

      <LegalSection id="sms-alerts" title="8. SMS alerts (Twilio 10DLC)">
        <p>
          EVIQ offers <strong>optional</strong> SMS text alerts for battery-related events on
          your vehicle. SMS is <strong>strictly transactional</strong>: we never send marketing
          or promotional messages by text. Categories include warranty-threshold risk,
          charging anomalies, phantom-drain upticks, heat warnings, and a weekly summary.
        </p>
        <p>
          To receive alerts you must complete a double opt-in inside the EVIQ app: enter
          your mobile number, tick the required consent box, and confirm a 6-digit
          verification code we text you. Message frequency is <strong>capped at 4 messages
          per month per user</strong>. Message and data rates may apply. SMS is delivered
          by our third-party provider Twilio under a registered 10DLC (Application-to-Person)
          campaign.
        </p>
        <p>
          You can stop receiving alerts at any time by tapping <em>Unsubscribe</em> on the
          Alerts screen, or by replying <strong>STOP</strong> to any EVIQ SMS. Reply{" "}
          <strong>HELP</strong> for support instructions. Revocation is honored immediately;
          we retain the consent record itself for regulatory audit purposes. See our{" "}
          <a href="/privacy#sms-communications">Privacy Policy &sect;8</a> for the full data
          handling terms.
        </p>
      </LegalSection>

      <LegalSection id="user-responsibilities" title="9. User responsibilities">
        <p>You are responsible for:</p>
        <ul>
          <li>Using EVIQ only for lawful purposes and in accordance with these Terms.</li>
          <li>
            Ensuring you have the legal right to connect any vehicle you authorize with EVIQ
            (for example, that it is your vehicle or that the registered owner has permitted
            you to do so).
          </li>
          <li>
            Maintaining the security of your Tesla account. EVIQ does not receive or store
            your Tesla password.
          </li>
          <li>
            Verifying important decisions (warranty claims, resale, service requests) with
            Tesla or a qualified professional. EVIQ&rsquo;s outputs are analytical estimates,
            not authoritative diagnoses.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="acceptable-use" title="10. Acceptable use">
        <p>You agree not to:</p>
        <ul>
          <li>Reverse-engineer, decompile, or attempt to extract the source of EVIQ.</li>
          <li>Scrape, spider, or automate access outside our published rate limits.</li>
          <li>Resell, sublicense, or rebrand EVIQ output without our prior written consent.</li>
          <li>
            Use EVIQ in any manner that could damage, disable, overburden, or impair the
            service, or interfere with any other party&rsquo;s use of the service.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="ip" title="11. Intellectual property">
        <p>
          EVIQ, EVIQ Score™, EVIQ Intelligence™, and EVIQ AI Engine™ are trademarks of
          EVIQ. The service, its underlying software, model logic, and content are the
          intellectual property of EVIQ or its licensors and are protected by copyright
          and other applicable laws.
        </p>
        <p>
          Tesla®, Model S®, Model 3®, Model X®, Model Y®, Cybertruck®, and related marks
          are trademarks of Tesla, Inc. EVIQ is an independent application and is not
          affiliated with, endorsed by, or sponsored by Tesla, Inc.
        </p>
      </LegalSection>

      <LegalSection id="warranty-disclaimer" title="12. Warranty disclaimer">
        <p>
          EVIQ IS PROVIDED &ldquo;<strong>AS IS</strong>&rdquo; AND &ldquo;<strong>AS
          AVAILABLE</strong>,&rdquo; WITHOUT WARRANTY OF ANY KIND, EITHER EXPRESS OR
          IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED WARRANTY OF MERCHANTABILITY,
          FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT
          EVIQ&rsquo;S OUTPUTS ARE ERROR-FREE, THAT DATA RETRIEVED FROM TESLA WILL BE
          UNINTERRUPTED, OR THAT ANY PREDICTIVE ESTIMATE (INCLUDING DEGRADATION,
          WARRANTY-CROSSOVER ETA, OR MARKETPLACE VALUE) WILL BE ACCURATE. EVIQ IS NOT A
          SUBSTITUTE FOR PROFESSIONAL VEHICLE SERVICE OR A MANUFACTURER-AUTHORIZED
          DIAGNOSTIC.
        </p>
      </LegalSection>

      <LegalSection id="liability" title="13. Limitation of liability">
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL EVIQ, ITS AFFILIATES,
          OR ITS SUPPLIERS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
          OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, GOODWILL, OR RANGE
          ESTIMATES, ARISING OUT OF OR RELATED TO YOUR USE OF EVIQ, EVEN IF ADVISED OF
          THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p>
          IN ALL CASES, EVIQ&rsquo;S TOTAL AGGREGATE LIABILITY FOR ANY CLAIM ARISING OUT
          OF OR RELATING TO EVIQ WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID
          EVIQ IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM, OR
          (B) ONE HUNDRED U.S. DOLLARS (US$100).
        </p>
      </LegalSection>

      <LegalSection id="indemnity" title="14. Indemnification">
        <p>
          You agree to defend, indemnify, and hold harmless EVIQ and its affiliates from
          any claim, damage, or expense (including reasonable attorneys&rsquo; fees)
          arising out of your breach of these Terms, your violation of any law, or your
          infringement of any third-party right.
        </p>
      </LegalSection>

      <LegalSection id="law" title="15. Governing law & disputes">
        <p>
          These Terms are governed by the laws of the State of Florida, without regard to
          its conflict-of-laws principles. Any dispute arising out of or relating to these
          Terms or EVIQ will be resolved exclusively in the state and federal courts
          located in Florida, and you consent to personal jurisdiction and
          venue there. You and EVIQ waive any right to a jury trial and to participate in
          a class action.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="16. Changes to these terms">
        <p>
          We may update these Terms from time to time. If we make material changes, we
          will notify you by email or in-app notice at least 14 days before they take
          effect. Continued use of EVIQ after the effective date constitutes acceptance of
          the updated Terms.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="17. Contact">
        <p>
          Questions about these Terms? Email{" "}
          <a href="mailto:support@eviq.pro">support@eviq.pro</a>.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
